import { useEffect, useRef, useState } from "react";
import { Maximize2, MapPin, X, ShieldCheck } from "lucide-react";

/**
 * Customer-facing satellite property preview, shown right under the address
 * field on /book after a valid address is selected.
 *
 * Renders ONLY:
 *   • Satellite imagery from Google Maps
 *   • Green overlay = detected mowable lawn area
 *   • Indigo overlay = lot perimeter (subtle)
 *   • Gray/blue overlay = house footprint (subtle)
 *   • "View Fullscreen" button that pops a full-bleed modal
 *
 * Does NOT show any of the following per iter42/43 spec:
 *   - lot size · acreage · square footage · confidence score · pricing calc
 *   - mowable area · house size · driveway area · per-sqft rates
 *
 * Falls back gracefully when GOOGLE_MAPS_API_KEY is missing.
 */
export default function CustomerPropertyPreview({ property }) {
  const [fullOpen, setFullOpen] = useState(false);
  if (!property?.lat || !property?.lng) return null;
  return (
    <>
      <div
        className="relative mt-2 rounded-2xl overflow-hidden border-2 border-emerald-200 shadow-sm group"
        style={{ height: 200 }}
        data-testid="customer-property-preview"
      >
        <MapCanvas property={property} interactive={false} />
        <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 bg-emerald-600/95 text-white rounded-full text-[10px] font-extrabold uppercase tracking-[0.14em] shadow" data-testid="customer-property-preview-badge">
          <ShieldCheck className="w-3 h-3" /> Property verified
        </div>
        <button
          type="button"
          onClick={() => setFullOpen(true)}
          className="absolute top-2 right-2 inline-flex items-center gap-1 px-2.5 py-1 bg-white/95 hover:bg-white text-gray-900 rounded-full text-[10px] font-extrabold uppercase tracking-[0.14em] shadow transition-colors"
          data-testid="customer-property-preview-fullscreen-btn"
          aria-label="View fullscreen"
        >
          <Maximize2 className="w-3 h-3" /> Fullscreen
        </button>
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2 text-[10px] font-bold pointer-events-none">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-black/55 text-white rounded-full">
            <span className="w-2 h-2 rounded-sm bg-emerald-400 border border-emerald-500" /> Lawn area
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-black/55 text-white rounded-full">
            <MapPin className="w-2.5 h-2.5 text-amber-300" /> Your property
          </span>
        </div>
      </div>

      {fullOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex flex-col"
          data-testid="customer-property-preview-fullscreen"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-between px-4 py-3 text-white">
            <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold">Property preview</div>
            <button
              type="button"
              onClick={() => setFullOpen(false)}
              className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
              data-testid="customer-property-preview-fullscreen-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 relative">
            <MapCanvas property={property} interactive={true} />
          </div>
          <div className="px-4 py-3 flex items-center justify-center gap-4 text-xs font-bold text-white">
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-400 border border-emerald-500" /> Lawn area</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-indigo-400/40 border border-indigo-500" /> Property boundary</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-amber-300" /> Your address</span>
          </div>
        </div>
      )}
    </>
  );
}

function MapCanvas({ property, interactive }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) { setReady(true); return; }
    if (window.google && window.google.maps) { setReady(true); return; }
    const existing = document.getElementById("gmaps-script");
    if (existing) {
      existing.addEventListener("load", () => setReady(true));
      return;
    }
    const s = document.createElement("script");
    s.id = "gmaps-script";
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=geometry`;
    s.async = true;
    s.defer = true;
    s.onload = () => setReady(true);
    document.head.appendChild(s);
  }, []);

  useEffect(() => {
    if (!ready || !ref.current || !property?.lat || !property?.lng) return;
    if (!(window.google && window.google.maps)) return;
    const center = { lat: Number(property.lat), lng: Number(property.lng) };
    const map = new window.google.maps.Map(ref.current, {
      center,
      zoom: 19,
      mapTypeId: "satellite",
      tilt: 0,
      disableDefaultUI: !interactive,
      gestureHandling: interactive ? "greedy" : "none",
      zoomControl: interactive,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      keyboardShortcuts: false,
    });
    new window.google.maps.Marker({ position: center, map, title: "Your property" });
    const mowable = Number(property.mowable_sqft || 0);
    const lot = Number(property.total_lot_sqft || property.square_feet || 0);
    const house = Number(property.house_sqft || property.building_footprint_sqft || 0);

    // Green overlay = mowable lawn area
    if (mowable > 0) {
      new window.google.maps.Circle({
        map, center, radius: Math.sqrt(mowable * 0.0929) * 0.6,
        strokeColor: "#10B981", strokeWeight: 2, strokeOpacity: 0.9,
        fillColor: "#10B981", fillOpacity: 0.35, clickable: false,
      });
    }
    // Indigo overlay = lot perimeter
    if (lot > 0 && lot > mowable) {
      new window.google.maps.Circle({
        map, center, radius: Math.sqrt(lot * 0.0929) * 0.65,
        strokeColor: "#6366F1", strokeWeight: 1.5, strokeOpacity: 0.6,
        fillColor: "#6366F1", fillOpacity: 0.06, clickable: false,
      });
    }
    // Gray overlay = house footprint
    if (house > 0) {
      new window.google.maps.Circle({
        map, center, radius: Math.sqrt(house * 0.0929) * 0.4,
        strokeColor: "#475569", strokeWeight: 1.5, strokeOpacity: 0.7,
        fillColor: "#94A3B8", fillOpacity: 0.45, clickable: false,
      });
    }
  }, [ready, property, interactive]);

  const apiKeyMissing = !process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (apiKeyMissing) {
    // Graceful fallback — Google Static Maps doesn't need JS API for a flat
    // image (but still needs a key for prod). Use OSM tile as a last resort
    // so the customer at least sees *something*.
    const z = 18;
    const fallback = `https://staticmap.openstreetmap.de/staticmap.php?center=${property.lat},${property.lng}&zoom=${z}&size=600x400&maptype=mapnik&markers=${property.lat},${property.lng},red-pushpin`;
    return (
      <img
        src={fallback}
        alt="Property preview"
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="customer-property-preview-fallback"
      />
    );
  }
  return <div ref={ref} className="absolute inset-0" data-testid="customer-property-preview-canvas" />;
}
